import React, { useState, useEffect } from 'react';
import TrophyDisplay from './TrophyDisplay';
import api from '../services/api';

/**
 * TrophyShelf component - displays earned trophies on an old-fashioned wooden shelf
 */
const TrophyShelf = ({ userId, profileId, className = '' }) => {
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);

  console.log('[TrophyShelf] Rendering with userId:', userId, 'profileId:', profileId);

  useEffect(() => {
    if (profileId) {
      fetchTrophies();
    } else {
      console.warn('[TrophyShelf] No profileId provided, skipping fetch');
      setLoading(false);
    }
  }, [profileId]);

  const fetchTrophies = async () => {
    try {
      setLoading(true);
      console.log('[TrophyShelf] Fetching trophies for profile:', profileId);
      const response = await api.get(`/trophies/profile/${profileId}`);
      console.log('[TrophyShelf] Fetched trophies:', response.data);
      setTrophies(response.data);
    } catch (error) {
      console.error('[TrophyShelf] Failed to fetch trophies:', error);
      console.error('[TrophyShelf] Error details:', error.response?.data);
      setTrophies([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`w-full py-8 ${className}`}>
        <div className="text-center text-gray-500">Loading trophies...</div>
      </div>
    );
  }

  if (trophies.length === 0) {
    return (
      <div className={`w-full ${className}`}>
        <div className="relative w-full" style={{ minHeight: '120px' }}>
          {/* Simple Shelf Line */}
          <div className="absolute bottom-8 left-0 right-0 h-2 rounded-sm" style={{
            background: '#654321',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}></div>

          {/* Empty State Message */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full py-8">
            <div className="text-gray-600 text-base font-semibold mb-1">
              🏆 Your Trophy Shelf
            </div>
            <div className="text-gray-500 text-sm">
              Complete sessions to earn trophies!
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="relative w-full" style={{ minHeight: '120px' }}>
        {(() => {
          // Group trophies by type, design, color, and nameplate_text
          const groupedTrophies = {};
          
          trophies.forEach(trophy => {
            const key = `${trophy.trophy_type}_${trophy.design}_${trophy.color}_${trophy.nameplate_text}`;
            if (!groupedTrophies[key]) {
              groupedTrophies[key] = {
                ...trophy,
                count: 0,
                trophyIds: []
              };
            }
            groupedTrophies[key].count += 1;
            groupedTrophies[key].trophyIds.push(trophy.trophy_id);
          });
          
          // Sort trophies by achievement level
          // Order (left to right): completion -> quiz_out -> round_win -> bowl_win (champion, farthest right)
          const achievementOrder = { 
            'completion': 1,
            'bonus': 2,
            'quiz_out': 3,
            'round_win': 4,
            'bowl_win': 5,
            'won_bowl': 5,
            'won_championship': 5
          };
          
          const trophyArray = Object.values(groupedTrophies).sort((a, b) => {
            // First sort by achievement type
            const aOrder = achievementOrder[a.trophy_type] || 0;
            const bOrder = achievementOrder[b.trophy_type] || 0;
            if (aOrder !== bOrder) return aOrder - bOrder;
            
            // If same achievement type, sort by color (bronze < silver < gold)
            const colorOrder = { 'bronze': 1, 'silver': 2, 'gold': 3 };
            return (colorOrder[a.color] || 0) - (colorOrder[b.color] || 0);
          });
          
          const trophiesPerRow = 6; // Maximum trophies per shelf
          const rows = [];
          
          // Split trophies into rows
          for (let i = 0; i < trophyArray.length; i += trophiesPerRow) {
            rows.push(trophyArray.slice(i, i + trophiesPerRow));
          }
          
          // Get badge color based on trophy color
          const getBadgeColor = (color) => {
            switch (color) {
              case 'gold':
                return 'bg-yellow-500 border-yellow-600';
              case 'silver':
                return 'bg-gray-400 border-gray-500';
              case 'bronze':
                return 'bg-amber-600 border-amber-700';
              default:
                return 'bg-gray-500 border-gray-600';
            }
          };
          
          // Helper function to abbreviate session title to first 4-6 words
          const abbreviateTitle = (title) => {
            if (!title) return '';
            const words = title.split(' ');
            if (words.length <= 6) return title;
            return words.slice(0, 5).join(' ') + '...';
          };
          
          return rows.map((row, rowIndex) => (
            <div key={rowIndex} className="relative w-full mb-8" style={{ minHeight: '150px' }}>
              {/* Shelf Line - positioned first as reference point */}
              <div 
                className="absolute left-0 right-0 h-2 rounded-sm" 
                style={{
                  background: '#654321',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  bottom: '40px',
                  zIndex: 1
                }}
              ></div>
              
              {/* Trophies Container - positioned to sit ON shelf (bottom: 42px = shelf top) */}
              <div 
                className="absolute left-0 right-0 px-4"
                style={{ bottom: '42px' }}
              >
                <div className="flex items-end justify-center gap-3">
                  {row.map((trophyGroup) => (
                    <div 
                      key={trophyGroup.trophyIds[0]}
                      className="flex flex-col items-center transition-transform hover:scale-110 relative"
                      style={{ zIndex: 10 }}
                    >
                      {/* Count Badge - only show if count > 1 */}
                      {trophyGroup.count > 1 && (
                        <div 
                          className={`absolute -top-1 -right-1 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold text-xs z-20 border border-white ${getBadgeColor(trophyGroup.color)}`}
                          style={{
                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                          }}
                        >
                          {trophyGroup.count}
                        </div>
                      )}
                      
                      <TrophyDisplay
                        design={trophyGroup.design}
                        color={trophyGroup.color}
                        nameplateText={null}  /* Text now displayed under shelf instead of on trophy */
                        fontSize={trophyGroup.font_size}
                        size="small"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Nameplate Text & Session Titles Under Shelf */}
              <div 
                className="absolute left-0 right-0 px-4"
                style={{ top: 'calc(100% - 38px)', zIndex: 10 }}
              >
                <div className="flex items-start justify-center gap-3">
                  {row.map((trophyGroup) => (
                    <div 
                      key={`title-${trophyGroup.trophyIds[0]}`}
                      className="flex flex-col items-center"
                      style={{ width: '96px' }} // Match trophy width
                    >
                      {/* Nameplate Text - displayed above session title */}
                      {trophyGroup.nameplate_text && (
                        <div 
                          className="font-bold text-center leading-tight mb-0.5"
                          style={{
                            fontSize: `${(trophyGroup.font_size || 0.75) * 0.9}rem`,
                            color: '#000000',
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%'
                          }}
                        >
                          {trophyGroup.nameplate_text}
                        </div>
                      )}
                      {/* Session Title */}
                      <div 
                        className="text-xs text-gray-600 text-center leading-tight"
                        style={{
                          wordWrap: 'break-word',
                          hyphens: 'auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {abbreviateTitle(trophyGroup.session_title)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ));
        })()}
      </div>
    </div>
  );
};

export default TrophyShelf;
